'use client';

import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { type LanguageCode, languageFromCode } from '@ifixit/i18n/languages';
import { toPhpLangId } from '@ifixit/i18n/php-langid';
import {
   type Country,
   type CountryArea,
   type UserLocaleCode,
   countriesByArea,
   parseUserLocale,
   routeLocaleByUserLocale,
} from '@ifixit/i18n/user-locale';
import { type ElementType, useMemo } from 'react';
import {
   useLocationPicker,
   useUserCountry,
   useUserLocale,
   useLocationNavigation,
   useIsCarpenter,
} from './location-provider';
import { Button } from './button';
import { type LinkLikeProps } from './context';
import { FaIcon } from './icon';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger, useSheet } from './sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { cn } from '../lib/utils';

interface LocationPickerProps {
   LinkComponent: ElementType<LinkLikeProps>;
}

export function LocationPicker({ LinkComponent }: LocationPickerProps) {
   const [userLocale, setUserLocale] = useUserLocale();
   const { isPickerOpen, openPicker, closePicker } = useLocationPicker();
   const tracking = useTracking();

   return (
      <Sheet
         open={isPickerOpen}
         onOpenChange={open => {
            if (open) {
               tracking.trackOpened();
               openPicker();
            } else {
               tracking.trackClosed();
               closePicker();
            }
         }}
      >
         <SheetTrigger asChild>
            <Button
               className="button-base button-icon hover:opacity-70"
               aria-label="Open location selector"
               data-testid="location-picker-trigger"
               data-user-locale={userLocale}
               onClick={tracking.trackToggleClick}
            >
               <FaIcon name="globe" style="regular" className="size-5 text-white" />
            </Button>
         </SheetTrigger>
         <SheetContent side="right" className="w-full sm:max-w-4xl p-0 flex flex-col gap-0">
            <div className="flex items-center gap-4 border-b border-gray-200 px-6 py-5">
               <div className="flex size-10 items-center justify-center rounded-full bg-brand-100">
                  <FaIcon name="globe" style="regular" className="size-5 text-brand-500" />
               </div>
               <div className="min-w-0 flex-1">
                  <SheetTitle className="text-base font-semibold text-gray-900">
                     Select your location
                  </SheetTitle>
                  <ActiveLocaleSubtitle userLocale={userLocale} />
               </div>
               <SheetClose asChild>
                  <Button
                     className="button-ghost button-icon -mr-2 shrink-0"
                     aria-label="Close"
                     onClick={tracking.trackToggleClick}
                  >
                     <FaIcon name="xmark" style="regular" className="size-4" />
                  </Button>
               </SheetClose>
            </div>
            <div className="px-4 pb-4 overflow-y-auto [&>:first-child]:border-t-0">
               {countriesByArea.map(group => (
                  <RegionSection
                     key={group.area}
                     group={group}
                     activeUserLocale={userLocale}
                     onSelect={setUserLocale}
                     LinkComponent={LinkComponent}
                  />
               ))}
            </div>
         </SheetContent>
      </Sheet>
   );
}

function ActiveLocaleSubtitle({ userLocale }: { userLocale: UserLocaleCode }) {
   const country = useUserCountry();
   const { language } = parseUserLocale(userLocale);

   return (
      <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500">
         <span className="truncate">
            {country.localName}
            {country.englishName && <span className="text-gray-400"> ({country.englishName})</span>}
         </span>
         <span className="text-gray-300" aria-hidden="true">
            &middot;
         </span>
         <span className="flex items-center gap-1 font-medium text-brand-600">
            <FaIcon name="check" className="size-2.5 text-brand-500" />
            {languageFromCode(language).localName}
         </span>
      </p>
   );
}

interface RegionSectionProps {
   group: CountryArea;
   activeUserLocale: UserLocaleCode;
   onSelect: (userLocale: UserLocaleCode) => Promise<unknown>;
   LinkComponent: ElementType<LinkLikeProps>;
}

function RegionSection({ group, activeUserLocale, onSelect, LinkComponent }: RegionSectionProps) {
   const { country: activeCountryCode } = parseUserLocale(activeUserLocale);
   const containsActiveCountry = group.countries.some(c => c.code === activeCountryCode);

   return (
      <Collapsible
         open={containsActiveCountry || undefined}
         className="border-t border-gray-200 last:border-b p-1"
      >
         <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg px-3 py-5 text-left transition-colors hover:bg-gray-100 cursor-default">
            <h3 className="m-0 p-0 text-xl leading-none font-medium text-gray-900">{group.area}</h3>
            <FaIcon
               name="chevron-down"
               className="size-4 text-gray-400 transition-all -rotate-90 group-open/collapsible:rotate-0 group-open/collapsible:text-gray-600"
            />
         </CollapsibleTrigger>
         <CollapsibleContent>
            <div className="columns-1 sm:columns-2 lg:columns-3 mt-1 px-1 pb-3">
               {group.countries.map(country => (
                  <CountryItem
                     key={country.code}
                     country={country}
                     activeUserLocale={activeUserLocale}
                     onSelect={onSelect}
                     LinkComponent={LinkComponent}
                  />
               ))}
            </div>
         </CollapsibleContent>
      </Collapsible>
   );
}

interface CountryItemProps {
   country: Country;
   activeUserLocale: UserLocaleCode;
   onSelect: (userLocale: UserLocaleCode) => Promise<unknown>;
   LinkComponent: ElementType<LinkLikeProps>;
}

function CountryItem({ country, activeUserLocale, onSelect, LinkComponent }: CountryItemProps) {
   const { hrefFromUserLocale } = useLocationNavigation();
   const isActive = country.locales.includes(activeUserLocale);

   return (
      <div
         className={cn(
            'relative p-3 rounded-lg transition-colors break-inside-avoid',
            isActive ? 'bg-brand-50' : 'hover:bg-gray-100'
         )}
      >
         <p className={cn('text-sm font-medium', isActive ? 'text-brand-900' : 'text-gray-900')}>
            {country.localName}
            {country.englishName && (
               <span className="ml-1.5 text-xs font-normal text-gray-400">
                  ({country.englishName})
               </span>
            )}
         </p>
         <div className="flex flex-wrap gap-x-4">
            {country.locales.map((userLocale, index) => (
               <LanguageLink
                  key={userLocale}
                  href={hrefFromUserLocale(userLocale)}
                  locale={userLocale}
                  countryName={country.localName}
                  isActive={userLocale === activeUserLocale}
                  isCountryDefault={index === 0}
                  onSelect={onSelect}
                  LinkComponent={LinkComponent}
               />
            ))}
         </div>
      </div>
   );
}

interface LanguageLinkProps {
   href: string | null;
   locale: UserLocaleCode;
   countryName: string;
   isActive: boolean;
   isCountryDefault?: boolean;
   onSelect: (locale: UserLocaleCode) => Promise<unknown>;
   LinkComponent: ElementType<LinkLikeProps>;
}

function LanguageLink({
   href,
   locale: userLocale,
   countryName,
   isActive,
   isCountryDefault,
   onSelect,
   LinkComponent,
}: LanguageLinkProps) {
   const { close } = useSheet();
   const { currentRouteLocale, withProgressBar } = useLocationNavigation();
   const { selectionMadeRef } = useLocationPicker();
   const tracking = useTracking();

   const targetRouteLocale = routeLocaleByUserLocale[userLocale];
   const { language } = parseUserLocale(userLocale);
   const languageName = languageFromCode(language as LanguageCode).localName;

   const className = cn(
      'text-sm no-underline transition-colors before:absolute before:content-[""]',
      isCountryDefault
         ? 'before:inset-0 before:rounded-lg'
         : 'relative z-10 before:-inset-x-2 before:-inset-y-1',
      isActive
         ? 'font-semibold text-brand-600 bg-linear-to-r from-brand-500 to-brand-500 bg-no-repeat bg-[length:100%_2px] bg-bottom'
         : 'font-medium text-gray-500 bg-linear-to-r from-gray-400 to-gray-400 bg-no-repeat bg-[length:0%_2px] bg-bottom transition-[background-size] duration-300 hover:bg-[length:100%_2px]'
   );
   const ariaLabel = `${languageName} — ${countryName}`;

   if (href != null) {
      const locale = targetRouteLocale === currentRouteLocale ? undefined : targetRouteLocale;
      return (
         <LinkComponent
            href={href}
            locale={locale}
            aria-label={ariaLabel}
            data-langid={language}
            data-user-locale={userLocale}
            className={className}
            onClick={async e => {
               selectionMadeRef.current = true;
               close();
               tracking.trackSelectLanguage({ language });
               if (LinkComponent === 'a') {
                  e.preventDefault();
                  await onSelect(userLocale);
                  window.location.href = href;
               } else {
                  void onSelect(userLocale);
               }
            }}
         >
            {languageName}
         </LinkComponent>
      );
   }

   return (
      <a
         href="/"
         aria-label={ariaLabel}
         data-langid={language}
         data-user-locale={userLocale}
         className={className}
         onClick={async e => {
            e.preventDefault();
            selectionMadeRef.current = true;
            close();
            tracking.trackSelectLanguage({ language });
            await withProgressBar(async () => {
               await onSelect(userLocale);
            });
            window.location.href = '/';
         }}
      >
         {languageName}
      </a>
   );
}

function useTracking() {
   const isCarpenter = useIsCarpenter();
   const { currentPhpLangId } = useLocationNavigation();
   const { selectionMadeRef } = useLocationPicker();

   return useMemo(() => {
      const app = isCarpenter ? 'Carpenter' : 'Next.js';

      function trackOpened() {
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_opened',
            eventName: app,
         });
      }

      function trackClosed() {
         const status = selectionMadeRef.current ? 'Selected' : 'Abandoned';
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_closed',
            eventName: `${app} - ${status}`,
         });
      }

      function trackToggleClick() {
         if (!isCarpenter) return;
         trackAnalyticsCustomEvent({
            eventCategory: 'Language',
            eventAction: 'Language Dropdown - Click',
         });
      }

      function trackSelectLanguage({ language }: { language: LanguageCode }) {
         const targetLangId = toPhpLangId(language);
         if (
            isCarpenter &&
            !canBypassGaEventFiringOnMobile() &&
            targetLangId !== currentPhpLangId
         ) {
            trackAnalyticsCustomEvent({
               eventCategory: 'Language',
               eventAction: `Language Switcher - ${currentPhpLangId} - ${targetLangId}`,
            });
         }
         trackAnalyticsCustomEvent({
            eventCategory: 'Locale Picker',
            eventAction: 'locale_picker_selection',
            eventName: `${app} - ${language}`,
         });
      }

      return { trackOpened, trackClosed, trackToggleClick, trackSelectLanguage };
   }, [isCarpenter, currentPhpLangId, selectionMadeRef]);
}

function canBypassGaEventFiringOnMobile(): boolean {
   const app = (window as { App?: { canIBypassGAEventFiringOnMobile?: boolean } }).App;
   return app?.canIBypassGAEventFiringOnMobile === true;
}
